import { AddRounded } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';

import { SearchBarComponent } from '@/components';
import { EpisodeCard, episodeGridStyles } from '@/containers';
import {
  selectPlan,
  selectUserCompliance,
  useFetchCatalogSearchResultsQuery,
  useFetchNewEpisodesQuery,
} from '@/store';
import { Referrer } from '@/types';

interface EpisodeSearchProps {
  addEpisode: (episodeId: string) => void;
  referrer: Referrer;
}
export const EpisodeSearch = ({ addEpisode, referrer }: EpisodeSearchProps) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const compliance = useSelector(selectUserCompliance);
  const plan = useSelector(selectPlan);

  const [searchTerm, setSearchTerm] = useState('');
  const isSearching = searchTerm !== '';

  const { data: searchResults, isFetching: isSearchResultsFetching } =
    useFetchCatalogSearchResultsQuery(
      {
        searchTerm,
        referrer,
        compliance,
        organizationName: plan?.data?.organizationName,
      },
      {
        skip: !isSearching,
      },
    );

  const { data: latestEpisodes, isFetching: isLatestEpisodesFetching } =
    useFetchNewEpisodesQuery(
      {
        compliance,
        limit: 40,
      },
      {
        skip: isSearching,
      },
    );

  const episodes = isSearching ? searchResults?.episodes : latestEpisodes;
  const isFetching = isSearching
    ? isSearchResultsFetching
    : isLatestEpisodesFetching;

  const onAddEpisodeClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    episodeId: string,
  ) => {
    event.stopPropagation();
    addEpisode(episodeId);
  };

  const iconHeight = isMobile ? 32 : 48;

  return (
    <Box>
      <Box>
        <Box
          sx={{
            bgcolor: 'white',
            position: 'sticky',
            zIndex: 'overlay',
            top: 0,
            pb: 2,
            mb: { xs: 2, md: 4 },
          }}>
          <Typography variant="h4" sx={{ mb: 2 }}>
            Search
          </Typography>
          <Box sx={{ width: { xs: '100%', md: 400 } }}>
            <SearchBarComponent
              onSearch={value => setSearchTerm(value || '')}
              placeholder={`Search for episodes ${isMobile ? '' : 'to add to your carousel'}`}
              fullWidth
            />
          </Box>
        </Box>
        <Box sx={episodeGridStyles.root}>
          {episodes?.map((episode, index) => (
            <Box
              key={`SearchResults-${episode.episodeId}`}
              sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                minWidth: 0,
              }}>
              <EpisodeCard
                stretch
                episode={episode}
                isLoading={isFetching}
                referrer={{ ...referrer, position: index + 1 }}
              />
              {!isFetching && (
                <Tooltip title="Add to playlist">
                  <IconButton
                    onClick={event =>
                      onAddEpisodeClick(event, episode.episodeId)
                    }
                    disableRipple
                    sx={{
                      position: 'absolute',
                      p: 0,
                      ml: 'auto',
                      top: 8,
                      right: 8,
                    }}>
                    <AddRounded
                      sx={{
                        width: iconHeight,
                        height: iconHeight,
                        color: 'white',
                        cursor: 'pointer',
                        backgroundColor: 'rgba(25, 0, 0, 0.3)',
                        borderRadius: '50%',
                        ml: 'auto',
                        p: 0.5,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        },
                      }}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
